import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { siteData } from '../data/mock';

export default function About() {
  const { about } = siteData;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.1, 1, 1.05]);

  return (
    <section id="about" ref={ref} className="section bg-nebula relative overflow-hidden">
      <div className="absolute inset-0 grid-lines opacity-20 pointer-events-none" />
      <div className="container relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 items-center">
          {/* Image with tilt */}
          <motion.div
            style={{ y }}
            className="md:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-[#d5b56b]/20">
              <motion.img
                style={{ scale, objectPosition: 'center 15%' }}
                src={about.image}
                alt={about.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05070f] via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#d5b56b] mb-2">
                  {about.label}
                </div>
                <div className="font-serif text-2xl text-[#eef1f8]">{about.name}</div>
                <div className="text-xs text-[#eef1f8]/60 mt-1">{about.role}</div>
              </div>
            </div>
            {/* Floating badge */}
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -top-6 -right-6 w-28 h-28 rounded-full glass grid place-items-center"
            >
              <div className="text-center">
                <div className="font-serif text-2xl text-[#d5b56b] leading-none">10+</div>
                <div className="text-[9px] tracking-[0.2em] uppercase text-[#eef1f8]/60 mt-1">Years Exp.</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Copy */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="text-xs tracking-[0.4em] uppercase text-[#d5b56b] mb-6">
                — Your Vision, Our Expertise
              </div>
              <h2 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight">
                <span className="italic text-[#eef1f8]">Vision.</span>
                <br />
                <span className="text-gold">Expertise.</span>
                <br />
                <span className="text-[#eef1f8]/80">Results.</span>
              </h2>
              <p className="mt-8 text-lg leading-relaxed text-[#eef1f8]/70 max-w-xl">
                {about.bio}
              </p>

              {/* Stats */}
              <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
                {[
                  { k: '150+', v: 'Projects Delivered' },
                  { k: '98%', v: 'Client Retention' },
                  { k: '24/7', v: 'Client Support' },
                ].map((s, i) => (
                  <motion.div
                    key={s.v}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="border-t border-[#d5b56b]/20 pt-4"
                  >
                    <div className="font-serif text-4xl text-[#d5b56b]">{s.k}</div>
                    <div className="text-xs text-[#eef1f8]/50 mt-2 tracking-wider">{s.v}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
